// src/components/admin/AdminConsultants.jsx
// ADMIN : créer les consultants, leur attribuer des créateurs,
// suivre ce qu'ils font (journal avant/après) et leur temps de travail.

import React, { useCallback, useEffect, useState } from "react";
import {
    UserPlus, Users, Clock, FileText, Download, KeyRound, Trash2, X, Search,
    PlusCircle, Pencil, Eye, ChevronDown, ChevronUp, Circle,
} from "lucide-react";
import {
    adminGetConsultants, adminCreerConsultant, adminResetPassword, adminSupprimerConsultant,
    adminAttribuerCreateurs, adminRechercheCreateurs, adminJournal, adminTemps, adminExportCsv,
    formatDuree,
} from "../../services/ConsultantService";

const VIOLET = "#481699";
const ilYa7Jours = () => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10);
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const heure = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const dateHeure = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const BADGE_ACTION = {
    ajout: { label: "Ajout", classe: "bg-green-100 text-green-700", Icone: PlusCircle },
    modification: { label: "Modification", classe: "bg-blue-100 text-blue-700", Icone: Pencil },
    consultation: { label: "Consultation", classe: "bg-gray-100 text-gray-600", Icone: Eye },
};

export default function AdminConsultants() {
    const [onglet, setOnglet] = useState("consultants");
    const [periode, setPeriode] = useState({ du: ilYa7Jours(), au: aujourdhui() });

    const [consultants, setConsultants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    const [modalCreation, setModalCreation] = useState(false);
    const [modalAttribution, setModalAttribution] = useState(null); // consultant
    const [motDePasseAffiche, setMotDePasseAffiche] = useState(null); // {nom, email, mdp}
    const [detailTemps, setDetailTemps] = useState(null); // consultant

    const charger = useCallback(async () => {
        setLoading(true);
        setErreur(null);
        try {
            const d = await adminGetConsultants(periode);
            setConsultants(d.consultants || []);
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
    }, [periode]);

    useEffect(() => { charger(); }, [charger]);

    const reset = async (c) => {
        if (!window.confirm(`Générer un nouveau mot de passe pour ${c.nom} ?`)) return;
        try {
            const d = await adminResetPassword(c.id);
            setMotDePasseAffiche({ nom: c.nom, email: c.email, mdp: d.mot_de_passe });
        } catch (e) { alert(e.message); }
    };

    const supprimer = async (c) => {
        if (!window.confirm(`Désactiver ${c.nom} ? Il ne pourra plus se connecter (son historique est conservé).`)) return;
        try { await adminSupprimerConsultant(c.id); charger(); } catch (e) { alert(e.message); }
    };

    const totaux = consultants.reduce(
        (t, c) => ({ ajouts: t.ajouts + c.ajouts, modifs: t.modifs + c.modifications, temps: t.temps + c.temps_secondes, enLigne: t.enLigne + (c.en_ligne ? 1 : 0) }),
        { ajouts: 0, modifs: 0, temps: 0, enLigne: 0 }
    );

    return (
        <div className="w-full max-w-6xl mx-auto px-4 py-6">
            {/* ══════════ EN-TÊTE ══════════ */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                <h1 className="text-2xl font-bold text-gray-800">Consultants</h1>
                <div className="lg:ml-auto flex flex-wrap items-center gap-2">
                    <input type="date" value={periode.du} max={periode.au}
                        onChange={(e) => setPeriode((p) => ({ ...p, du: e.target.value }))}
                        className="px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                    <span className="text-gray-400 text-sm">→</span>
                    <input type="date" value={periode.au} min={periode.du}
                        onChange={(e) => setPeriode((p) => ({ ...p, au: e.target.value }))}
                        className="px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                    <button onClick={() => setModalCreation(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold"
                        style={{ backgroundColor: VIOLET }}>
                        <UserPlus size={16} /> Nouveau consultant
                    </button>
                </div>
            </div>

            {/* ══════════ CHIFFRES CLÉS ══════════ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                {[
                    ["En ligne maintenant", totaux.enLigne, "text-green-600"],
                    ["Événements ajoutés", totaux.ajouts, "text-gray-900"],
                    ["Modifications", totaux.modifs, "text-gray-900"],
                    ["Temps de travail", formatDuree(totaux.temps), "text-gray-900"],
                ].map(([l, v, c]) => (
                    <div key={l} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                        <p className="text-xs text-gray-500">{l}</p>
                        <p className={`text-2xl font-extrabold ${c}`}>{v}</p>
                    </div>
                ))}
            </div>

            {/* ══════════ ONGLETS ══════════ */}
            <div className="flex gap-1 mb-4 border-b border-gray-200">
                {[["consultants", "Équipe", Users], ["journal", "Journal d'activité", FileText]].map(([v, l, I]) => (
                    <button key={v} onClick={() => setOnglet(v)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${onglet === v ? "border-purple-700 text-purple-800" : "border-transparent text-gray-500"}`}>
                        <I size={16} /> {l}
                    </button>
                ))}
            </div>

            {erreur && <div className="bg-red-50 text-red-600 rounded-xl p-3 mb-4 text-sm">{erreur}</div>}

            {onglet === "consultants" && (
                loading ? <p className="text-gray-400 text-center py-12">Chargement…</p> :
                consultants.length === 0 ? (
                    <p className="text-gray-400 text-center py-12 bg-white rounded-2xl border">Aucun consultant. Créez-en un.</p>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {consultants.map((c) => (
                            <div key={c.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                                <div className="flex items-start gap-3">
                                    <div className="relative">
                                        <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold bg-purple-100" style={{ color: VIOLET }}>
                                            {c.nom?.charAt(0)?.toUpperCase()}
                                        </div>
                                        <Circle size={12} className={`absolute -bottom-0.5 -right-0.5 ${c.en_ligne ? "fill-green-500 text-white" : "fill-gray-300 text-white"}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-800">{c.nom}</p>
                                        <p className="text-xs text-gray-400 truncate">{c.email}</p>
                                        <p className="text-xs mt-1">
                                            {c.en_ligne
                                                ? <span className="text-green-600 font-semibold">● En ligne</span>
                                                : <span className="text-gray-400">Dernière activité : {dateHeure(c.derniere_activite)}</span>}
                                        </p>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => reset(c)} title="Nouveau mot de passe" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><KeyRound size={16} /></button>
                                        <button onClick={() => supprimer(c)} title="Désactiver" className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                                    </div>
                                </div>

                                {/* Aujourd'hui */}
                                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-gray-50 rounded-xl p-2">
                                        <p className="text-gray-400">Début aujourd'hui</p>
                                        <p className="font-bold text-gray-800">{heure(c.debut_aujourdhui)}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-2">
                                        <p className="text-gray-400">En ligne aujourd'hui</p>
                                        <p className="font-bold text-gray-800">{formatDuree(c.temps_aujourdhui)}</p>
                                    </div>
                                </div>

                                {/* Période */}
                                <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                                    <div className="rounded-xl p-2 bg-green-50"><p className="text-lg font-extrabold text-green-700">{c.ajouts}</p><p className="text-[11px] text-green-700">ajouts</p></div>
                                    <div className="rounded-xl p-2 bg-blue-50"><p className="text-lg font-extrabold text-blue-700">{c.modifications}</p><p className="text-[11px] text-blue-700">modifs</p></div>
                                    <button onClick={() => setDetailTemps(c)} className="rounded-xl p-2 bg-purple-50 hover:bg-purple-100">
                                        <p className="text-lg font-extrabold" style={{ color: VIOLET }}>{formatDuree(c.temps_secondes)}</p>
                                        <p className="text-[11px]" style={{ color: VIOLET }}>{c.jours_travailles} jour{c.jours_travailles > 1 ? "s" : ""} · détail</p>
                                    </button>
                                </div>

                                {/* Créateurs */}
                                <div className="mt-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <p className="text-xs font-semibold text-gray-500">Créateurs attribués ({c.createurs.length})</p>
                                        <button onClick={() => setModalAttribution(c)} className="text-xs font-semibold" style={{ color: VIOLET }}>Gérer</button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {c.createurs.length === 0 && <span className="text-xs text-orange-500">Aucun : il ne peut rien faire</span>}
                                        {c.createurs.map((cr) => (
                                            <span key={cr.id} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{cr.nom}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}

            {onglet === "journal" && <Journal consultants={consultants} periode={periode} />}

            {modalCreation && (
                <ModalCreation
                    onClose={() => setModalCreation(false)}
                    onCree={(d) => { setModalCreation(false); setMotDePasseAffiche(d); charger(); }}
                />
            )}
            {modalAttribution && (
                <ModalAttribution
                    consultant={modalAttribution}
                    onClose={() => setModalAttribution(null)}
                    onSave={() => { setModalAttribution(null); charger(); }}
                />
            )}
            {motDePasseAffiche && <ModalMotDePasse info={motDePasseAffiche} onClose={() => setMotDePasseAffiche(null)} />}
            {detailTemps && <ModalTemps consultant={detailTemps} periode={periode} onClose={() => setDetailTemps(null)} />}
        </div>
    );
}

// ══════════════════════════════════════════════════════════
//  JOURNAL D'ACTIVITÉ
// ══════════════════════════════════════════════════════════
function Journal({ consultants, periode }) {
    const [filtres, setFiltres] = useState({ consultant_id: "", createur_id: "", action: "" });
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [loading, setLoading] = useState(false);
    const [ouvert, setOuvert] = useState(null);

    const createurs = Array.from(
        new Map(consultants.flatMap((c) => c.createurs).map((cr) => [cr.id, cr])).values()
    );

    const charger = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const d = await adminJournal({ ...filtres, ...periode, page });
            setLogs(d.logs || []);
            setPagination(d.pagination);
        } finally {
            setLoading(false);
        }
    }, [filtres, periode]);

    useEffect(() => { charger(1); }, [charger]);

    const select = "px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white";

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-4">
                <select className={select} value={filtres.consultant_id} onChange={(e) => setFiltres((f) => ({ ...f, consultant_id: e.target.value }))}>
                    <option value="">Tous les consultants</option>
                    {consultants.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                <select className={select} value={filtres.createur_id} onChange={(e) => setFiltres((f) => ({ ...f, createur_id: e.target.value }))}>
                    <option value="">Tous les créateurs</option>
                    {createurs.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                <select className={select} value={filtres.action} onChange={(e) => setFiltres((f) => ({ ...f, action: e.target.value }))}>
                    <option value="">Toutes les actions</option>
                    <option value="ajout">Ajouts</option>
                    <option value="modification">Modifications</option>
                    <option value="consultation">Consultations</option>
                </select>
                <button onClick={() => adminExportCsv({ ...filtres, ...periode }).catch((e) => alert(e.message))}
                    className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-purple-400">
                    <Download size={16} /> Export CSV
                </button>
            </div>

            {loading ? <p className="text-gray-400 text-center py-12">Chargement…</p> :
            logs.length === 0 ? <p className="text-gray-400 text-center py-12 bg-white rounded-2xl border">Aucune activité sur la période.</p> : (
                <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
                    {logs.map((l) => {
                        const b = BADGE_ACTION[l.action] || BADGE_ACTION.consultation;
                        const nbChamps = l.changements ? Object.keys(l.changements).length : 0;
                        const estOuvert = ouvert === l.id;
                        return (
                            <div key={l.id} className="p-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-xs text-gray-400 w-28 shrink-0">{dateHeure(l.created_at)}</span>
                                    <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${b.classe}`}>
                                        <b.Icone size={12} /> {b.label}
                                    </span>
                                    {!l.succes && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-600">Échec</span>}
                                    <span className="text-sm text-gray-800">
                                        <strong>{l.consultant?.nomComplet}</strong>
                                        <span className="text-gray-400"> pour </span>
                                        <strong>{l.createur?.nomComplet}</strong>
                                        {l.titre_evenement && <><span className="text-gray-400"> · </span>« {l.titre_evenement} »</>}
                                    </span>
                                    {nbChamps > 0 && (
                                        <button onClick={() => setOuvert(estOuvert ? null : l.id)}
                                            className="ml-auto flex items-center gap-1 text-xs font-semibold" style={{ color: VIOLET }}>
                                            {nbChamps} champ{nbChamps > 1 ? "s" : ""} {estOuvert ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        </button>
                                    )}
                                </div>
                                {estOuvert && (
                                    <div className="mt-2 overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-left text-gray-400">
                                                    <th className="py-1 pr-3 font-medium">Champ</th>
                                                    <th className="py-1 pr-3 font-medium">Avant</th>
                                                    <th className="py-1 font-medium">Après</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(l.changements).map(([champ, v]) => (
                                                    <tr key={champ} className="border-t border-gray-100 align-top">
                                                        <td className="py-1.5 pr-3 font-mono text-gray-500">{champ}</td>
                                                        <td className="py-1.5 pr-3 text-red-600 line-through break-all">{v.avant ?? "—"}</td>
                                                        <td className="py-1.5 text-green-700 break-all">{v.apres ?? "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {pagination.last_page > 1 && (
                <div className="flex justify-center items-center gap-3 mt-4 text-sm">
                    <button disabled={pagination.current_page === 1} onClick={() => charger(pagination.current_page - 1)} className="px-3 py-1 rounded-lg border disabled:opacity-40">Précédent</button>
                    <span>{pagination.current_page} / {pagination.last_page}</span>
                    <button disabled={pagination.current_page === pagination.last_page} onClick={() => charger(pagination.current_page + 1)} className="px-3 py-1 rounded-lg border disabled:opacity-40">Suivant</button>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════
//  MODALES
// ══════════════════════════════════════════════════════════
function Modal({ titre, onClose, children, large }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className={`bg-white rounded-2xl w-full ${large ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h3 className="font-bold text-gray-800">{titre}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function ModalCreation({ onClose, onCree }) {
    const [form, setForm] = useState({ nomComplet: "", email: "", password: "" });
    const [envoi, setEnvoi] = useState(false);
    const [erreur, setErreur] = useState(null);
    const input = "w-full px-3 py-2.5 rounded-xl border border-gray-200 text-base sm:text-sm outline-none focus:border-purple-500";

    const valider = async (e) => {
        e.preventDefault();
        setEnvoi(true);
        setErreur(null);
        try {
            const d = await adminCreerConsultant(form);
            onCree({ nom: d.consultant.nom, email: d.consultant.email, mdp: d.mot_de_passe });
        } catch (err) {
            setErreur(err.message);
        } finally {
            setEnvoi(false);
        }
    };

    return (
        <Modal titre="Nouveau consultant" onClose={onClose}>
            <form onSubmit={valider} className="flex flex-col gap-3">
                <input className={input} placeholder="Nom complet" required value={form.nomComplet} onChange={(e) => setForm({ ...form, nomComplet: e.target.value })} />
                <input className={input} type="email" placeholder="E-mail" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className={input} type="text" placeholder="Mot de passe (vide = généré automatiquement)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                {erreur && <p className="text-sm text-red-600">{erreur}</p>}
                <button disabled={envoi} className="py-2.5 rounded-xl text-white font-semibold disabled:opacity-60" style={{ backgroundColor: VIOLET }}>
                    {envoi ? "Création…" : "Créer le compte"}
                </button>
            </form>
        </Modal>
    );
}

function ModalMotDePasse({ info, onClose }) {
    const texte = `Espace consultant Thrundrz\nE-mail : ${info.email}\nMot de passe : ${info.mdp}`;
    return (
        <Modal titre="Identifiants" onClose={onClose}>
            <p className="text-sm text-gray-600 mb-3">
                Transmettez ces identifiants à <strong>{info.nom}</strong>. Le mot de passe ne sera <strong>plus affiché</strong>.
            </p>
            <div className="bg-gray-50 rounded-xl p-3 font-mono text-sm mb-3 whitespace-pre-wrap break-all">{texte}</div>
            <button onClick={() => navigator.clipboard.writeText(texte)} className="w-full py-2.5 rounded-xl text-white font-semibold" style={{ backgroundColor: VIOLET }}>
                Copier
            </button>
        </Modal>
    );
}

function ModalAttribution({ consultant, onClose, onSave }) {
    const [choisis, setChoisis] = useState(consultant.createurs);
    const [q, setQ] = useState("");
    const [resultats, setResultats] = useState([]);
    const [envoi, setEnvoi] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => adminRechercheCreateurs(q).then(setResultats).catch(() => {}), 300);
        return () => clearTimeout(t);
    }, [q]);

    const basculer = (cr) =>
        setChoisis((liste) => (liste.some((x) => x.id === cr.id) ? liste.filter((x) => x.id !== cr.id) : [...liste, cr]));

    const enregistrer = async () => {
        setEnvoi(true);
        try {
            await adminAttribuerCreateurs(consultant.id, choisis.map((c) => c.id));
            onSave();
        } catch (e) {
            alert(e.message);
        } finally {
            setEnvoi(false);
        }
    };

    return (
        <Modal titre={`Créateurs de ${consultant.nom}`} onClose={onClose} large>
            <div className="flex flex-wrap gap-1.5 mb-3 min-h-[32px]">
                {choisis.length === 0 && <span className="text-sm text-gray-400">Aucun créateur sélectionné</span>}
                {choisis.map((c) => (
                    <button key={c.id} onClick={() => basculer(c)} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full text-white" style={{ backgroundColor: VIOLET }}>
                        {c.nom} <X size={12} />
                    </button>
                ))}
            </div>
            <div className="relative mb-2">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un créateur (nom ou e-mail)…"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-base sm:text-sm outline-none focus:border-purple-500" />
            </div>
            <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y">
                {resultats.map((cr) => {
                    const coche = choisis.some((x) => x.id === cr.id);
                    return (
                        <label key={cr.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={coche} onChange={() => basculer(cr)} />
                            <span className="text-sm font-medium text-gray-800">{cr.nom}</span>
                            <span className="text-xs text-gray-400 truncate">{cr.email}</span>
                        </label>
                    );
                })}
            </div>
            <button onClick={enregistrer} disabled={envoi} className="w-full mt-4 py-2.5 rounded-xl text-white font-semibold disabled:opacity-60" style={{ backgroundColor: VIOLET }}>
                {envoi ? "Enregistrement…" : `Enregistrer (${choisis.length})`}
            </button>
        </Modal>
    );
}

function ModalTemps({ consultant, periode, onClose }) {
    const [jours, setJours] = useState(null);
    useEffect(() => {
        adminTemps(consultant.id, periode).then((d) => setJours(d.jours || [])).catch(() => setJours([]));
    }, [consultant.id, periode]);

    return (
        <Modal titre={`Temps de travail · ${consultant.nom}`} onClose={onClose} large>
            {!jours ? <p className="text-gray-400 text-center py-6">Chargement…</p> :
            jours.length === 0 ? <p className="text-gray-400 text-center py-6">Aucune activité sur la période.</p> : (
                <div className="flex flex-col gap-3">
                    {jours.map((j) => (
                        <div key={j.jour} className="border border-gray-200 rounded-xl p-3">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                <p className="font-bold text-gray-800 capitalize">
                                    {new Date(j.jour).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                                </p>
                                <span className="text-sm text-gray-500 flex items-center gap-1"><Clock size={14} /> {j.premiere} → {j.derniere}</span>
                                <span className="ml-auto text-sm font-extrabold" style={{ color: VIOLET }}>{formatDuree(j.temps_secondes)} en ligne</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{j.ajouts} ajout(s) · {j.modifications} modification(s) · {j.nb_sessions} session(s)</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {j.sessions.map((s, i) => (
                                    <span key={i} className="text-[11px] px-2 py-1 rounded-lg bg-gray-100 text-gray-600">
                                        {s.debut}–{s.fin} ({formatDuree(s.duree_secondes)})
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}